/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import APIManager from "../../ApiManager/ApiManager";
import {
  FETCH_DOMAIN_APPROVER_ECU_REQUEST,
  FETCH_DOMAIN_APPROVER_ECU_SUCCESS,
  FETCH_DOMAIN_APPROVER_ECU_FAILURE,
} from "./DomainApproverEcuTypes";
import { COMMON_ERROR } from "../ApplicationDetailsError/ApplicationDetailsErrorTypes";

const ECU_TYPES_CACHE_KEY = 'ecu-types-cache';
const ECU_TYPES_CACHE_TIMESTAMP_KEY = 'ecu-types-cache-timestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

const safeParseCache = () => {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const cached = sessionStorage.getItem(ECU_TYPES_CACHE_KEY);
    const timestamp = sessionStorage.getItem(ECU_TYPES_CACHE_TIMESTAMP_KEY);
    
    if (!cached || !timestamp) return null;

    // Check if cache has expired
    const cacheTime = Number.parseInt(timestamp, 10);
    const currentTime = Date.now();
    if (currentTime - cacheTime > CACHE_DURATION_MS) {
      // Cache expired
      sessionStorage.removeItem(ECU_TYPES_CACHE_KEY);
      sessionStorage.removeItem(ECU_TYPES_CACHE_TIMESTAMP_KEY);
      return null;
    }

    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to parse ECU types cache', error);
    return null;
  }
};

const persistCache = (ecuTypes) => {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(ECU_TYPES_CACHE_KEY, JSON.stringify(ecuTypes));
    sessionStorage.setItem(ECU_TYPES_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to persist ECU types cache', error);
  }
};

// Clear cache to force refresh
const clearCache = () => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(ECU_TYPES_CACHE_KEY);
    sessionStorage.removeItem(ECU_TYPES_CACHE_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Failed to clear ECU types cache', error);
  }
};

export const fetchDomainApproverEcuRequest = () => {
  return {
    type: FETCH_DOMAIN_APPROVER_ECU_REQUEST,
  };
};

export const fetchDomainApproverEcuSuccess = (EcuData) => {
  return {
    type: FETCH_DOMAIN_APPROVER_ECU_SUCCESS,
    payload: EcuData,
  };
};

export const fetchDomainApproverEcuFailure = (error) => {
  return {
    type: FETCH_DOMAIN_APPROVER_ECU_FAILURE,
    payload: error,
  };
};

export const fetchDomainApproverEcuDetails = () => {
  return async function (dispatch) {
    // Check cache first
    const cachedEcuTypes = safeParseCache();
    if (cachedEcuTypes && cachedEcuTypes.length > 0) {
      dispatch(fetchDomainApproverEcuSuccess(cachedEcuTypes));
      return cachedEcuTypes;
    }

    dispatch(fetchDomainApproverEcuRequest());
    try {
      const data = await APIManager.getDomainApproverEcuTypes();
      persistCache(data);
      dispatch(fetchDomainApproverEcuSuccess(data));
      return data;
    } catch (error) {
      console.error("ECU fetch error:", error);
      dispatch(fetchDomainApproverEcuFailure(error));
      dispatch(commonError(error, FETCH_DOMAIN_APPROVER_ECU_FAILURE));
      throw error;
    }
  };
};

// Force refresh ECU types data by clearing cache and fetching fresh data
export const forceFreshEcuDetails = () => {
  return async function (dispatch) {
    clearCache();
    dispatch(fetchDomainApproverEcuRequest());
    try {
      const data = await APIManager.getDomainApproverEcuTypes();
      persistCache(data);
      dispatch(fetchDomainApproverEcuSuccess(data));
      return data;
    } catch (error) {
      console.error("ECU fresh fetch error:", error);
      dispatch(fetchDomainApproverEcuFailure(error));
      dispatch(commonError(error, FETCH_DOMAIN_APPROVER_ECU_FAILURE));
      throw error;
    }
  };
};

export const commonError=(error,errorType)=>{
  return({
    type:COMMON_ERROR,
    payload:{
      message:error.message,
      errorType:errorType
    }
  })
}
